import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { packingListService } from '../../../services/packingListService';

const PackingList = ({ trip }) => {
  const [packingItems, setPackingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    quantity: 1,
    priority: 'normal'
  });

  useEffect(() => {
    if (trip?.id) {
      loadPackingList();
    }
  }, [trip?.id]);

  const loadPackingList = async () => {
    try {
      setLoading(true);
      setError('');
      const items = await packingListService?.getPackingList(trip?.id);
      setPackingItems(items);
    } catch (err) {
      console.error('Error loading packing list:', err);
      setError(err?.message || 'Failed to load packing list');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (itemId, currentChecked) => {
    try {
      await packingListService?.toggleItemChecked(itemId, !currentChecked);
      
      // Optimistic update
      setPackingItems(prev => prev?.map(item => 
        item?.id === itemId ? { ...item, isChecked: !currentChecked } : item
      ));
    } catch (err) {
      console.error('Error toggling item:', err);
      setError(err?.message || 'Failed to update item');
      await loadPackingList(); // Reload on error
    }
  };

  const handleGenerateList = async (keepManualItems = false) => {
    try {
      setIsGenerating(true);
      setError('');
      setShowRegenerateConfirm(false);
      
      // If keeping manual items, first fetch them
      let manualItems = [];
      if (keepManualItems) {
        const currentItems = await packingListService?.getPackingList(trip?.id);
        manualItems = currentItems?.filter(item => !item?.autoGenerated);
      }
      
      // Generate new list
      await packingListService?.generatePackingList(trip?.id);
      
      // Re-add manual items if requested
      if (keepManualItems && manualItems?.length > 0) {
        for (const item of manualItems) {
          await packingListService?.addCustomItem(
            trip?.id,
            item?.itemName,
            item?.itemCategory,
            item?.itemQuantity,
            item?.itemPriority
          );
        }
      }
      
      await loadPackingList();
    } catch (err) {
      console.error('Error generating packing list:', err);
      setError(err?.message || 'Failed to generate packing list');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShowRegenerateConfirm = () => {
    // Check if there are any manual items
    const hasManualItems = packingItems?.some(item => !item?.autoGenerated);
    
    if (hasManualItems) {
      setShowRegenerateConfirm(true);
    } else {
      handleGenerateList(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem?.name?.trim() || !newItem?.category?.trim()) {
      setError('Item name and category are required');
      return;
    }

    try {
      setError('');
      await packingListService?.addCustomItem(
        trip?.id,
        newItem?.name?.trim(),
        newItem?.category?.trim(),
        newItem?.quantity,
        newItem?.priority
      );
      
      // Reset form and close
      setNewItem({ name: '', category: '', quantity: 1, priority: 'normal' });
      setShowAddForm(false);
      
      // Reload list
      await loadPackingList();
    } catch (err) {
      console.error('Error adding item:', err);
      setError(err?.message || 'Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      setError('');
      await packingListService?.deleteItem(itemId);
      
      // Optimistic update
      setPackingItems(prev => prev?.filter(item => item?.id !== itemId));
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err?.message || 'Failed to delete item');
      await loadPackingList(); // Reload on error
    }
  };

  // Group items by category
  const groupedItems = packingItems?.reduce((acc, item) => {
    if (!acc?.[item?.itemCategory]) {
      acc[item.itemCategory] = [];
    }
    acc?.[item?.itemCategory]?.push(item);
    return acc;
  }, {});

  const categories = ['All', ...Object.keys(groupedItems)?.sort()];

  const filteredItems = selectedCategory === 'All' 
    ? packingItems 
    : packingItems?.filter(item => item?.itemCategory === selectedCategory);

  const checkedCount = packingItems?.filter(item => item?.isChecked)?.length;
  const totalCount = packingItems?.length;
  const progressPercentage = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'essential': return 'text-destructive';
      case 'important': return 'text-warning';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-organic p-6 shadow-organic">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Package" size={20} color="var(--color-accent)" />
          <h3 className="font-heading font-heading-bold text-lg text-card-foreground">
            Packing List
          </h3>
        </div>
        {packingItems?.length === 0 && !loading && (
          <Button
            variant="default"
            size="sm"
            onClick={() => handleGenerateList(false)}
            disabled={isGenerating}
            iconName={isGenerating ? "Loader" : "Sparkles"}
            iconPosition="left"
          >
            {isGenerating ? 'Generating...' : 'Generate List'}
          </Button>
        )}
      </div>
      {/* Progress bar */}
      {packingItems?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">
              {checkedCount} of {totalCount} packed
            </span>
            <span className="font-medium text-primary">
              {progressPercentage}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-organic">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading packing list...</span>
        </div>
      ) : packingItems?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground mb-4">
            No packing list generated yet. Click the button above to create a smart packing list based on your trip details.
          </p>
        </div>
      ) : (
        <>
          {/* Category filter */}
          <div className="mb-4 flex flex-wrap gap-2">
            {categories?.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 text-xs rounded-organic transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category}
                {category !== 'All' && ` (${groupedItems?.[category]?.length || 0})`}
              </button>
            ))}
          </div>

          {/* Add Item Button */}
          {!showAddForm && (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(true)}
                iconName="Plus"
                iconPosition="left"
                className="w-full"
              >
                Add Manual Item
              </Button>
            </div>
          )}

          {/* Add Item Form */}
          {showAddForm && (
            <div className="mb-4 p-4 bg-muted/30 rounded-organic border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm text-card-foreground">Add Custom Item</h4>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewItem({ name: '', category: '', quantity: 1, priority: 'normal' });
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              
              <div className="space-y-3">
                <Input
                  placeholder="Item name"
                  value={newItem?.name}
                  onChange={(e) => setNewItem(prev => ({ ...prev, name: e?.target?.value }))}
                  className="w-full"
                />
                
                <Input
                  placeholder="Category (e.g., Clothing, Electronics)"
                  value={newItem?.category}
                  onChange={(e) => setNewItem(prev => ({ ...prev, category: e?.target?.value }))}
                  className="w-full"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Quantity"
                    value={newItem?.quantity}
                    onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e?.target?.value) || 1 }))}
                    className="w-full"
                  />
                  
                  <Select
                    value={newItem?.priority}
                    onChange={(e) => setNewItem(prev => ({ ...prev, priority: e?.target?.value }))}
                    className="w-full"
                  >
                    <option value="normal">Normal</option>
                    <option value="important">Important</option>
                    <option value="essential">Essential</option>
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddItem}
                    className="flex-1"
                  >
                    Add Item
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewItem({ name: '', category: '', quantity: 1, priority: 'normal' });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Items list */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredItems?.map(item => (
              <div
                key={item?.id}
                className={`flex items-center space-x-3 p-3 rounded-organic transition-colors ${
                  item?.isChecked 
                    ? 'bg-muted/30' :'bg-background hover:bg-muted/20'
                }`}
              >
                <Checkbox
                  checked={item?.isChecked}
                  onChange={() => handleToggleItem(item?.id, item?.isChecked)}
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className={`font-body text-sm ${
                      item?.isChecked 
                        ? 'line-through text-muted-foreground' 
                        : 'text-card-foreground'
                    }`}>
                      {item?.itemName}
                    </p>
                    {item?.itemQuantity > 1 && (
                      <span className="text-xs text-muted-foreground">
                        x{item?.itemQuantity}
                      </span>
                    )}
                    {!item?.autoGenerated && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Manual
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {item?.itemCategory}
                    </span>
                    {item?.itemPriority !== 'normal' && (
                      <span className={`text-xs ${getPriorityColor(item?.itemPriority)}`}>
                        {item?.itemPriority}
                      </span>
                    )}
                  </div>
                </div>
                {!item?.autoGenerated && (
                  <button
                    onClick={() => handleDeleteItem(item?.id)}
                    className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                    title="Delete item"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Regenerate button */}
          <div className="mt-4 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowRegenerateConfirm}
              disabled={isGenerating}
              iconName={isGenerating ? "Loader" : "RefreshCw"}
              iconPosition="left"
              className="w-full"
            >
              {isGenerating ? 'Regenerating...' : 'Regenerate List'}
            </Button>
          </div>
        </>
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-organic p-6 max-w-md w-full mx-4 shadow-organic">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="AlertCircle" size={24} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading font-heading-bold text-lg text-card-foreground mb-2">
                  Regenerate Packing List?
                </h3>
                <p className="text-sm text-muted-foreground">
                  You have manually added items. Do you want to keep them or start fresh?
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => handleGenerateList(true)}
                disabled={isGenerating}
                className="w-full"
              >
                Keep Manual Items
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleGenerateList(false)}
                disabled={isGenerating}
                className="w-full"
              >
                Start Fresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRegenerateConfirm(false)}
                disabled={isGenerating}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingList;